﻿import * as _ from "lodash";

import type {
    CategorySearchKey,
    ICategories,
    ICategory,
    IFlattenedCategory,
} from "app/common/services/categoryService.types";
import { isHidden } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import { categoryTextToIconFilter, i18nCategoryFilter } from "app/filters/ngFilters";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import RegexPatterns from "app/services/RegexPatterns";

import { OLD_TO_NEW_CATEGORIES_MAP } from "./oldToNewCategoriesMap";

export enum ECategoryNameValidation {
    LEGAL,
    ILLEGAL_EXIST,
    ILLEGAL_CHARS,
    ILLEGAL_LENGTH,
}
const TITLE_MAX_LENGTH = 26;
export const CATEGORY_PREFIX = "$";
export const SUB_CATEGORY_DELIMITER = "/";

export const CATEGORY_PARENT_AND_NAME_DELIMITER = "~";
const SUB_CATEGORY_DISPLAY_DELIMITER = ">";

export const ALL = "All";
const ALL_INDUSTRY_FOR_API = CATEGORY_PREFIX + ALL;
export const ADULT_ID = "Adult";

let categories: ICategories;

export interface ICategoryListOptions {
    // Hides the Adult industry from selection lists. Lookups stay unfiltered so
    // existing URLs and saved links pointing at Adult keep resolving.
    excludeAdult?: boolean;
}

export interface ICategoryService {
    categoryQueryParamToCategoryObject: (category: string) => ICategory;
    getCategory: (categoryKey: string, searchBy?: string) => ICategory;
    getCategories: (withAll?: boolean, options?: ICategoryListOptions) => ICategories;
    getFlattenedCategoriesList: (
        includeSharedCustomCategories?: boolean,
        excludeCustomCategoriesWithSegments?: boolean,
        options?: ICategoryListOptions,
    ) => ICategories;
    categoryTextToIcon: (text: string) => string;
    hasSmartSharingPermission: () => boolean;
    hasAdvancedCustomIndustriesPermission: () => boolean;
}
const CUSTOM_CATEGORY_DEFAULT_ID = "custom_categories";
const CUSTOM_CATEGORY_DEFAULT_ICON = "custom-category";
const CUSTOM_CATEGORY_DEFAULT_ICON_CLASS = "sw-icon-custom-categories";
export const createCategoryService = () => {
    function isInactive(id: FixmeType) {
        return swSettings.current.isDemo === true && id !== swSettings.current.demo.category;
    }

    function buildCategories(withAll = true) {
        if (categories) {
            return categories;
        }
        const categoryArray: ICategory[] = _.extend(window.similarweb.config.categories);
        if (categoryArray) {
            categoryArray.forEach(function (category: ICategory) {
                if (category.id == ALL) {
                    return;
                }
                const requestChild = true;
                const parent = category;

                category.children = [];
                // @ts-ignore FIXME: strict migration - 'category.name' possibly undefined — legacy invariant, name always set on top-level categories from window.similarweb.config
                category.id = category.name;
                // @ts-ignore FIXME: strict migration - 'category.name' possibly undefined — legacy invariant, name always set on top-level categories
                category.categoryId = category.name;
                category.text = i18nCategoryFilter()(category.name);
                category.icon = "sprite-category " + category.name;
                category.cssClass = "parent-category";
                category.inactive = isInactive(category.id);
                category.forApi = CATEGORY_PREFIX + category.id;
                category.forUrl = category.id;
                // @ts-ignore FIXME: strict migration - 'category.name' possibly undefined — legacy invariant, name always set on top-level categories
                category.forDisplayApi = category.name;
                _.forEach(category.sons, function (category) {
                    const id = parent.name + CATEGORY_PARENT_AND_NAME_DELIMITER + category.name;
                    parent.children.push({
                        id,
                        categoryId: id,
                        text: i18nCategoryFilter()(parent.name + SUB_CATEGORY_DELIMITER + category.name, requestChild),
                        inactive: isInactive(id),
                        getText() {
                            return i18nCategoryFilter()(parent.name + SUB_CATEGORY_DELIMITER + category.name);
                        },
                        getChildrenText() {
                            return i18nCategoryFilter()(category.name);
                        },
                        isCustomCategory: false,
                        isOldCategory: !!OLD_TO_NEW_CATEGORIES_MAP[id],
                        forApi: CATEGORY_PREFIX + parent.name + SUB_CATEGORY_DELIMITER + category.name,
                        forUrl: id,
                        forDisplayApi: category.name,
                        icon: "sprite-category " + parent.name,
                    });
                });
            });
            if (!_.find(categoryArray, { id: ALL }) && withAll) {
                categoryArray.unshift({
                    id: ALL,
                    categoryId: ALL,
                    forApi: ALL_INDUSTRY_FOR_API,
                    forUrl: ALL,
                    forDisplayApi: ALL,
                    children: [],
                    cssClass: "parent-category",
                    text: i18nFilter()("forms.category.all"),
                    inactive: isInactive(ALL),
                    icon: "sw-icon-categories",
                    isCustomCategory: false,
                    // @ts-ignore FIXME: strict migration - 'null' not assignable to 'string' — legacy synthetic ALL entry has no real lastUpdated
                    lastUpdated: null,
                });
            }
        }
        categories = categoryArray;
        return categoryArray;
    }

    // Filters the returned list rather than the cached array: `buildCategories` memoizes
    // its result process-wide, so filtering inside it would let the first caller decide
    // the list for everyone. Without options the cached instance is returned as before.
    function getCategories(withAll = true, options?: ICategoryListOptions) {
        const categoriesList = buildCategories(withAll);
        return options?.excludeAdult ? categoriesList.filter((category) => category.id !== ADULT_ID) : categoriesList;
    }

    function getCategoriesTitle() {
        return i18nFilter()("swcategories.dropdown.title");
    }
    function getCategory(categoryKey: string, searchBy: CategorySearchKey = "id") {
        if (categoryKey) {
            if (categoryKey.indexOf("*") !== -1) {
                return _.find(UserCustomCategoryService.getCustomCategories(), { [searchBy]: categoryKey });
            } else {
                const flatten: FixmeType[] = [];
                getCategories().forEach((item) => {
                    flatten.push(item);
                    if (item.children) {
                        item.children.forEach((subItem: FixmeType) => flatten.push(subItem));
                    }
                });

                return _.find(flatten, { [searchBy]: categoryKey });
            }
        }
        return undefined;
    }

    function validateGroupName(name: string) {
        const userCategories = UserCustomCategoryService.getCustomCategories();
        if (RegexPatterns.testCustomCategoryNameUnicode(name)) {
            return ECategoryNameValidation.ILLEGAL_CHARS;
        }
        if (userCategories.some((category) => category.text === name)) {
            return ECategoryNameValidation.ILLEGAL_EXIST;
        }
        if (name.length > TITLE_MAX_LENGTH) {
            return ECategoryNameValidation.ILLEGAL_LENGTH;
        }

        return ECategoryNameValidation.LEGAL;
    }

    function getNextCategoryName(name: string) {
        let newName = name;
        let suffix = 1;
        // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site via createCategoryService return object
        while (this.validateGroupName(newName) === ECategoryNameValidation.ILLEGAL_EXIST) {
            newName = `${name}${suffix++}`;
        }
        return newName;
    }

    function hasCustomCategoriesPermission() {
        return swSettings.components.Home.resources.IsCCAllowed || false;
    }

    function hasAdvancedCustomIndustriesPermission(): boolean {
        return !isHidden(swSettings.components.BuilderIndustryTab) && !isHidden(swSettings.components.BuilderTermsTab);
    }

    function hasSmartSharingPermission() {
        return swSettings.components.IndustryAnalysis.resources.IndustriesSmartSharingFeature === "Open" || false;
    }

    function categoryTextToIcon(text: string) {
        return categoryTextToIconFilter()(text);
    }
    function parseCategoryFromApiToDropDown(
        { id, text, count, children }: FixmeType,
        isCustomCategory: boolean,
        parentId = null,
    ) {
        return {
            text,
            count,
            name: text,
            id,
            isCustomCategory,
            isChild: parentId ? true : false,
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site via createCategoryService return object
            icon: isCustomCategory ? CUSTOM_CATEGORY_DEFAULT_ICON : this.categoryTextToIcon(text),
            forApi: id,
            parentId,
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
            sons: children?.map((child: FixmeType) => this.parseCategoryFromApiToDropDown(child, isCustomCategory, id)),
        };
    }

    function parseCategoryFromApiToCategoriesFilter(
        { id, text, count, children }: FixmeType,
        isCustomCategory: boolean,
        parentId = null,
        totalCountArray: FixmeType[] = [],
    ) {
        totalCountArray.push(count);
        return {
            text,
            count,
            name: text,
            id,
            isCustomCategory,
            isChild: parentId ? true : false,
            icon: isCustomCategory ? CUSTOM_CATEGORY_DEFAULT_ICON : "sprite-category " + text,
            forApi: id,
            parentId,
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
            sons: children?.map((child: FixmeType) => this.parseCategoryFromApiToDropDown(child, isCustomCategory, id)),
        };
    }

    function categoriesSortByCountDesc({ count: categoryACount }: FixmeType, { count: categoryBCount }: FixmeType) {
        if (categoryACount > categoryBCount) {
            return -1;
        }
        if (categoryACount < categoryBCount) {
            return 1;
        }
        return 0;
    }

    function parseCategoriesFromApiToCategoriesFilter(categoriesObject: {
        categories: ICategory[];
        allCustomCategories: ICategory[];
    }) {
        const { categories, allCustomCategories } = categoriesObject;
        const addCategoryID = (category: FixmeType) => ({ ...category, categoryId: category.id });
        const { sharedCustomIndustries, customIndustries } = UserCustomCategoryService.splitCustomCategoriesArray(
            allCustomCategories.map(addCategoryID),
        );
        const totalCountArray: number[] = [];
        const categoriesForCategoriesFilter = categories.map((category) =>
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
            this.parseCategoryFromApiToCategoriesFilter(category, false, null, totalCountArray),
        );
        const categoriesForCategoriesFilterWithoutSons: FixmeType[] = [];
        categoriesForCategoriesFilter.map((category) => {
            categoriesForCategoriesFilterWithoutSons.push(category);
            category.sons
                // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
                ?.sort(this.categoriesSortByCountDesc)
                .map((son: FixmeType) => categoriesForCategoriesFilterWithoutSons.push(son));
            delete category.sons;
        });
        const parseCustomCategory = (category: FixmeType) => ({
            ...category,
            icon: CUSTOM_CATEGORY_DEFAULT_ICON_CLASS,
            isCustomCategory: true,
            forApi: category.id,
        });

        return {
            categories: [
                {
                    id: ALL,
                    children: [],
                    cssClass: "parent-category",
                    text: "All Industries ",
                    inactive: false,
                    icon: "sw-icon-categories",
                    isCustomCategory: false,
                    isChild: false,
                    props: {},
                    count: totalCountArray.reduce((sum, current) => sum + current, Number()),
                },
                ...categoriesForCategoriesFilterWithoutSons,
            ],
            customCategories: customIndustries.map(parseCustomCategory),
            sharedCustomCategories: sharedCustomIndustries.map(parseCustomCategory),
        };
    }

    function getFlattenedCategoriesList(
        includeSharedCustomCategories = false,
        excludeCustomCategoriesWithSegments = false,
        options?: ICategoryListOptions,
    ): IFlattenedCategory[] {
        let customCategories = includeSharedCustomCategories
            ? UserCustomCategoryService.getCustomAndSharedCategories()
            : UserCustomCategoryService.getCustomCategories();
        if (excludeCustomCategoriesWithSegments) {
            customCategories = customCategories.filter((category) => !category.segments?.length);
        }
        return [
            ...customCategories.map((item: ICategory) => ({
                ...item,
                isChild: false,
            })),
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
            ...this.getCategories(true, options).reduce((items: IFlattenedCategory[], item: ICategory) => {
                const { children = [] } = item;
                return [
                    ...items,
                    { ...item, isChild: false },
                    ...children.map((child: FixmeType) => ({
                        ...child,
                        isChild: true,
                        parentItem: item,
                    })),
                ];
            }, []),
        ];
    }

    function categoryQueryParamToCategoryObject(categoryQueryParam: string): ICategory {
        if (UserCustomCategoryService.isCustomCategory(categoryQueryParam)) {
            const id = Array.isArray(categoryQueryParam)
                ? categoryQueryParam[0].substring(1)
                : categoryQueryParam.substring(1);
            // @ts-ignore FIXME: strict migration - 'ICategory | undefined' not assignable to 'ICategory' — legacy contract, caller treats undefined as not found
            return UserCustomCategoryService.getCustomAndSharedCategoryById(id);
        } else {
            // @ts-ignore FIXME: strict migration - 'this' implicitly any — legacy method-style function, this bound at call site
            return this.getCategory(categoryQueryParam);
        }
    }

    return {
        getCategories,
        getCategoriesTitle,
        getCategory,
        validateGroupName,
        getFlattenedCategoriesList,
        hasCustomCategoriesPermission,
        getNextCategoryName,
        categoriesSortByCountDesc,
        parseCategoryFromApiToCategoriesFilter,
        parseCategoryFromApiToDropDown,
        categoryTextToIcon,
        parseCategoriesFromApiToCategoriesFilter,
        categoryQueryParamToCategoryObject,
        hasSmartSharingPermission,
        hasAdvancedCustomIndustriesPermission,
    };
};

export default createCategoryService();
